<script setup lang="ts">
import { useLayout } from '@/composables/layout'
import AppConfigurator from './AppConfigurator.vue'
import { useMemberStore } from '@/stores/member'
import { useSiteStore } from '@/stores/site'
import LocaleSwitch from '@/components/LocaleSwitch.vue'
import FairChoicer from '@/components/FairChoicer.vue'
import { useI18n } from 'vue-i18n'
import { computed, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { useFairStore } from '@/stores/fair'

const { onMenuToggle, toggleDarkMode, isDarkTheme } = useLayout()
const { t } = useI18n()
const router = useRouter()

const isDevelopment = import.meta.env.DEV
const memberStore = useMemberStore()
const siteStore = useSiteStore()
const fairStore = useFairStore()

const $viewModel = reactive({
  confirmHeader: '',
  confirmMessage: ''
})

const handlers = {
  logout: async () => {
    $viewModel.confirmHeader = t('layout.topbar.logout')
    $viewModel.confirmMessage = t('layout.topbar.logout_confirm')

    if (
      await common.confirm($viewModel.confirmMessage, {
        header: $viewModel.confirmHeader,
        group: 'dismissible'
      })
    ) {
      await memberStore.logout()
    }
  },
  goToAccount: () => {
    // 계정 정보 페이지로 이동
    router.push('/account')
  }
}

const logo = computed(() => siteStore.logoUrl ?? common.getDefaultLogo())
</script>

<template>
  <div class="layout-topbar dark:!border-[var(--p-surface-800)]">
    <div class="layout-topbar-logo-container">
      <button v-show="fairStore.fair" class="layout-menu-button layout-topbar-action" @click="onMenuToggle">
        <i class="pi pi-bars" />
      </button>
      <RouterLink to="/" class="layout-topbar-logo">
        <img class="h-11 object-contain ms-2" :src="logo" :alt="siteStore.siteConfig.siteName" />
      </RouterLink>
    </div>

    <div class="layout-topbar-fairs hidden lg:flex lg:items-center lg:justify-center">
      <span>{{ t('layout.topbar.current_fair') }}</span>
      <FairChoicer class="max-w-64 lg:max-w-max" />
    </div>

    <div class="layout-topbar-actions">
      <div v-if="isDevelopment" class="layout-config-menu">
        <button type="button" class="layout-topbar-action" @click="toggleDarkMode">
          <i :class="['pi', { 'pi-moon': isDarkTheme, 'pi-sun': !isDarkTheme }]" />
        </button>
        <div class="relative">
          <button
            v-styleclass="{
              selector: '@next',
              enterFromClass: 'hidden',
              enterActiveClass: 'animate-scalein',
              leaveToClass: 'hidden',
              leaveActiveClass: 'animate-fadeout',
              hideOnOutsideClick: true
            }"
            type="button"
            class="layout-topbar-action layout-topbar-action-highlight me-3">
            <i class="pi pi-palette" />
          </button>
          <AppConfigurator />
        </div>
      </div>

      <button
        v-styleclass="{
          selector: '@next',
          enterFromClass: 'hidden',
          enterActiveClass: 'animate-scalein',
          leaveToClass: 'hidden',
          leaveActiveClass: 'animate-fadeout',
          hideOnOutsideClick: true
        }"
        class="layout-topbar-menu-button layout-topbar-action">
        <i class="pi pi-ellipsis-v" />
      </button>

      <div class="layout-topbar-menu hidden lg:block">
        <div class="layout-topbar-menu-content">
          <LocaleSwitch v-if="isDevelopment" />

          <div
            class="flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-md transition-colors"
            @click="handlers.goToAccount">
            <Avatar class="!bg-primary !text-white" icon="pi pi-user" size="large" shape="circle" />
            <span class="text-lg" style="line-height: 1">{{ memberStore.loginMember.companyName }}</span>
          </div>

          <Button class="exhibitor-button" severity="secondary" @click="handlers.logout">
            <i class="pi pi-sign-out mr-1" />
            <span class="text-lg">{{ t('layout.topbar.logout') }}</span>
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<i18n lang="yaml">
ko:
  layout:
    topbar:
      current_fair: '현재 관리중인 전시회'
      logout: '로그아웃'
      logout_confirm: '로그아웃 하시겠습니까?'
en:
  layout:
    topbar:
      current_fair: 'Current Fair'
      logout: 'Logout'
      logout_confirm: 'Are you sure you want to logout?'
</i18n>
